import React, { useState, useEffect } from 'react';
import { getUrl } from 'aws-amplify/storage';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import './PartnersGrid.css';

interface Partner {
  id: string;
  name: string;
  logoPath?: string;
  website?: string;
  person?: string;
  email?: string;
  description?: string;
  isActive: boolean;
  displayOrder?: number;
}

const PartnersGrid: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerLogoUrls, setPartnerLogoUrls] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(true);

  const client = generateClient<Schema>({ authMode: 'iam' });

  // Fetch partners from database
  const fetchPartners = async () => {
    try {
      console.log('🔍 Fetching partners for PartnersGrid...');
      const result = await client.models.Partner.list({
        selectionSet: ['id', 'name', 'logoPath', 'website', 'person', 'email', 'description', 'isActive', 'displayOrder']
      });
      
      if (result.data) {
        // Filter active partners and sort by displayOrder
        const activePartners = result.data
          .filter((partner: any) => partner.isActive)
          .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .slice(0, 14); // Limit to 14 for display
        
        console.log('✅ Partners fetched for PartnersGrid:', activePartners.length);
        setPartners(activePartners as Partner[]);
      }
    } catch (err) {
      console.error('🚨 Error fetching partners for PartnersGrid:', err);
      // Don't set error state as partners are not critical for page functionality
    } finally {
      setLoading(false);
    }
  };

  // Handle partner logo click
  const handlePartnerClick = (partner: Partner) => {
    if (partner.website) {
      window.open(partner.website, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // Resolve partner logo URLs
  useEffect(() => {
    const resolvePartnerLogos = async () => {
      const logoUrls: {[key: string]: string} = {};
      
      for (const partner of partners) {
        if (partner.logoPath) {
          try {
            if (partner.logoPath.startsWith('http') || partner.logoPath.startsWith('/')) {
              logoUrls[partner.id] = partner.logoPath;
            } else {
              // Use S3 URLs with local fallback
              try {
                const publicPath = partner.logoPath.startsWith('public/') ? partner.logoPath : `public/${partner.logoPath}`;
                const { url } = await getUrl({ path: publicPath });
                logoUrls[partner.id] = url.toString();
              } catch (s3Error) {
                console.warn(`S3 access failed for ${partner.name}, using local fallback:`, s3Error);
                // Fallback to local assets if S3 fails
                const fallbackPath = partner.logoPath.replace('public/', '/assets/images/');
                logoUrls[partner.id] = fallbackPath;
              }
            }
          } catch (e) {
            console.warn(`Failed to resolve logo for partner ${partner.name}:`, e);
            // Fallback to local assets
            const fallbackPath = partner.logoPath.replace('public/', '/assets/images/');
            logoUrls[partner.id] = fallbackPath;
          }
        }
      }
      
      setPartnerLogoUrls(logoUrls);
    };

    if (partners.length > 0) {
      resolvePartnerLogos();
    }
  }, [partners]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
    target.alt = 'Partner Logo Placeholder';
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  return (
    <section className="partners-grid-section" data-section="ki-kurse_partners-grid" data-page="ki-kurse">
      <div className="container partners-grid-container">
        <div className="partners-grid-header">
          <h2>Unser <span className="highlight-text">Partnernetzwerk</span></h2>
        </div>
        <div className="partners-grid">
          {loading ? (
            // Loading placeholder
            Array.from({ length: 8 }, (_, index) => (
              <div key={`loading-${index}`} className="partner-grid-item loading">
                <div className="loading-placeholder"></div>
              </div>
            ))
          ) : (
            partners.map((partner) => {
              const logoUrl = partnerLogoUrls[partner.id];
              if (!logoUrl) return null;
              
              return (
                <div 
                  key={partner.id} 
                  className={`partner-grid-item ${partner.website ? 'clickable' : ''}`}
                  onClick={() => handlePartnerClick(partner)}
                  title={partner.website ? `Besuchen Sie ${partner.name}` : partner.name}
                >
                  <img
                    src={logoUrl}
                    alt={`${partner.name} Logo`}
                    className="partner-grid-image"
                    onError={handleImageError}
                    onDragStart={handleDragStart}
                    onContextMenu={handleContextMenu}
                    draggable={false}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnersGrid;
